import { useEffect, useLayoutEffect, useMemo, useRef, type MutableRefObject } from 'react';
import { Polygon, Tooltip as LeafletTooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import type { LatLngExpression } from 'leaflet';

import { cn } from '@/lib/utils';
import type { AreaMapShape } from '@/lib/areas';
import {
  axisBasisDegrees,
  METERS_PER_DEG_LAT,
  metersPerDegLng,
  placementOvalLatLngs,
  placementRectangleLatLngs,
} from './location-map-shared';

function logAreaLayer(phase: string, detail?: Record<string, unknown>) {
  // Verbose by design; filter DevTools console by `AreaPlacementLayer`.
  console.debug('[AreaPlacementLayer]', phase, detail ?? '');
}

export type AreaPlacement = {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  shape: AreaMapShape;
  widthM: number;
  heightM: number;
  orientationDegrees: number;
};

type AreaPolygonsStyle = {
  color: string;
  weight: number;
  fillColor: string;
  fillOpacity: number;
};

const DEFAULT_AREA_PALETTE = ['#2563eb', '#16a34a', '#c026d3', '#ea580c', '#0891b2', '#ca8a04'];

function areaColor(index: number, palette: readonly string[]): string {
  return palette[index % palette.length] ?? palette[0] ?? '#2563eb';
}

function areaDotIcon(color: string, selected: boolean) {
  return L.divIcon({
    className: cn('flex items-center justify-center !border-0 !bg-transparent', selected && 'drop-shadow-md'),
    html: `<div style="width:14px;height:14px;border-radius:9999px;background:${color};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.35)"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

function handleIconWidth(color: string) {
  return L.divIcon({
    className: '!border-0 !bg-transparent',
    html: `<div style="width:8px;height:14px;border-radius:2px;background:${color};border:2px solid white;box-shadow:0 1px 2px rgba(0,0,0,0.4)"></div>`,
    iconSize: [14, 8],
    iconAnchor: [7, 4],
  });
}

function handleIconHeight(color: string) {
  return L.divIcon({
    className: '!border-0 !bg-transparent',
    html: `<div style="width:14px;height:8px;border-radius:2px;background:${color};border:2px solid white;box-shadow:0 1px 2px rgba(0,0,0,0.4)"></div>`,
    iconSize: [8, 14],
    iconAnchor: [4, 7],
  });
}

function handleIconOrientation(color: string, orientationDegrees: number) {
  const sh = 'drop-shadow(0 1px 1px rgba(0,0,0,0.45))';
  const arrows = `<div data-orientation-icon-rotate="true" style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;filter:${sh};transform:rotate(${orientationDegrees}deg);transform-origin:center center"><div style="width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-bottom:7px solid ${color}"></div><div style="width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:7px solid ${color}"></div></div>`;
  return L.divIcon({
    className: '!border-0 !bg-transparent',
    html: `<div style="width:26px;height:26px;display:flex;align-items:center;justify-content:center;overflow:visible">${arrows}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}

function orientationIconMarkerRoot(marker: L.Marker): HTMLElement | null {
  const m = marker as unknown as { getElement?: () => HTMLElement; _icon?: HTMLElement | null };
  return m.getElement?.() ?? m._icon ?? null;
}

function applyOrientationIconRotationDom(marker: L.Marker, orientationDegrees: number): boolean {
  const root = orientationIconMarkerRoot(marker);
  const inner = root?.querySelector('[data-orientation-icon-rotate]');
  if (!(inner instanceof HTMLElement)) return false;
  inner.style.transform = `rotate(${orientationDegrees}deg)`;
  return true;
}

function metersDeltaFromCenter(
  cornerLat: number,
  cornerLng: number,
  centerLat: number,
  centerLng: number,
): { de: number; dn: number } {
  const dLat = cornerLat - centerLat;
  const dLng = cornerLng - centerLng;
  const dn = dLat * METERS_PER_DEG_LAT;
  const de = dLng * metersPerDegLng(centerLat);
  return { de, dn };
}

function orientationDegreesFromHandleRay(
  centerLat: number,
  centerLng: number,
  handleLat: number,
  handleLng: number,
): number {
  const { de, dn } = metersDeltaFromCenter(handleLat, handleLng, centerLat, centerLng);
  let deg = (Math.atan2(de, dn) * 180) / Math.PI;
  if (deg < 0) deg += 360;
  let o = deg - 90;
  if (o < 0) o += 360;
  if (o >= 360) o -= 360;
  return o;
}

function heightResizeHandlePosition(p: AreaPlacement): [number, number] {
  const { ue, un } = axisBasisDegrees(p.orientationDegrees);
  const halfW = p.heightM / 2;
  const mLat = METERS_PER_DEG_LAT;
  const mLng = metersPerDegLng(p.latitude);
  const de = halfW * ue;
  const dn = halfW * un;
  return [p.latitude + dn / mLat, p.longitude + de / mLng];
}

function widthResizeHandlePosition(p: AreaPlacement): [number, number] {
  const { ve, vn } = axisBasisDegrees(p.orientationDegrees);
  const halfH = p.widthM / 2;
  const mLat = METERS_PER_DEG_LAT;
  const mLng = metersPerDegLng(p.latitude);
  const de = halfH * ve;
  const dn = halfH * vn;
  return [p.latitude + dn / mLat, p.longitude + de / mLng];
}

const ORIENTATION_HANDLE_ARM_M = 22;

function orientationHandlePosition(p: AreaPlacement): [number, number] {
  const arm = Math.max(ORIENTATION_HANDLE_ARM_M, Math.max(p.widthM, p.heightM) / 2 + 10);
  const t = ((p.orientationDegrees + 90) * Math.PI) / 180;
  const ue = Math.sin(t);
  const un = Math.cos(t);
  const mLat = METERS_PER_DEG_LAT;
  const mLng = metersPerDegLng(p.latitude);
  const de = arm * ue;
  const dn = arm * un;
  return [p.latitude + dn / mLat, p.longitude + de / mLng];
}

function ImperativeAreaCenterMarkers({
  placed,
  selectedId,
  editable,
  paneNames,
  palette,
  onSelect,
  onCenterDrag,
  onCenterDragEnd,
  suspendFitDuringHandleDragRef,
}: {
  placed: AreaPlacement[];
  selectedId: string | null;
  editable: boolean;
  paneNames: { centers: string; centersSelected: string };
  palette: readonly string[];
  onSelect?: (areaId: string) => void;
  onCenterDrag?: (areaId: string, lat: number, lng: number) => void;
  onCenterDragEnd?: (areaId: string, lat: number, lng: number) => void | Promise<void>;
  suspendFitDuringHandleDragRef?: MutableRefObject<boolean>;
}) {
  const map = useMap();
  const markersRef = useRef<Record<string, L.Marker>>({});
  const placedRef = useRef(placed);
  placedRef.current = placed;
  const handlersRef = useRef({ onSelect, onCenterDrag, onCenterDragEnd, suspendFitDuringHandleDragRef });
  handlersRef.current = { onSelect, onCenterDrag, onCenterDragEnd, suspendFitDuringHandleDragRef };

  const placedSig = useMemo(
    () =>
      JSON.stringify(
        placed.map((p) => ({
          id: p.id,
          lat: p.latitude,
          lng: p.longitude,
        })),
      ),
    [placed],
  );

  useEffect(() => {
    logAreaLayer('centerMarkers.effect start', { editable, selectedId, placedCount: placedRef.current.length });
    const placedNow = placedRef.current;

    const nextIds = new Set(placedNow.map((p) => p.id));
    for (const [id, m] of Object.entries(markersRef.current)) {
      if (!nextIds.has(id)) {
        map.removeLayer(m);
        delete markersRef.current[id];
      }
    }

    placedNow.forEach((p, idx) => {
      const isSel = selectedId === p.id;
      const color = areaColor(idx, palette);
      const icon = areaDotIcon(color, isSel);
      const zIndexOffset = isSel ? 800 : 400;
      const pane = isSel ? paneNames.centersSelected : paneNames.centers;

      const existing = markersRef.current[p.id];
      if (!existing) {
        const m = L.marker([p.latitude, p.longitude], {
          icon,
          draggable: editable,
          pane,
          zIndexOffset,
          bubblingMouseEvents: false,
        }).addTo(map);
        m.bindTooltip(p.name, { direction: 'top', offset: [0, -8], opacity: 0.9 });
        if (handlersRef.current.onSelect) {
          m.on('click', (e: L.LeafletEvent) => {
            L.DomEvent.stopPropagation(e);
            handlersRef.current.onSelect?.(p.id);
          });
        }
        if (editable && handlersRef.current.onCenterDrag && handlersRef.current.onCenterDragEnd) {
          m.on('dragstart', (e: L.LeafletEvent) => {
            L.DomEvent.stopPropagation(e);
            const suspend = handlersRef.current.suspendFitDuringHandleDragRef;
            if (suspend) suspend.current = true;
          });
          m.on('drag', (e: L.LeafletEvent) => {
            L.DomEvent.stopPropagation(e);
            const mm = e.target as L.Marker;
            const ll = mm.getLatLng();
            handlersRef.current.onCenterDrag?.(p.id, ll.lat, ll.lng);
          });
          m.on('dragend', (e: L.LeafletEvent) => {
            L.DomEvent.stopPropagation(e);
            const mm = e.target as L.Marker;
            const ll = mm.getLatLng();
            void handlersRef.current.onCenterDragEnd?.(p.id, ll.lat, ll.lng);
          });
        }
        markersRef.current[p.id] = m;
      } else {
        existing.setLatLng([p.latitude, p.longitude]);
        existing.setIcon(icon);
        existing.setZIndexOffset(zIndexOffset);
      }
    });

    return () => {
      for (const m of Object.values(markersRef.current)) map.removeLayer(m);
      markersRef.current = {};
    };
  }, [map, placedSig, selectedId, editable, paneNames.centers, paneNames.centersSelected, palette, placed]);

  return null;
}

function ImperativeResizeHandleMarkers({
  committed,
  liveCenter,
  iconOrientationDegrees,
  color,
  paneName,
  suspendFitDuringHandleDragRef,
  onHeightHandleDrag,
  onHeightHandleDragEnd,
  onWidthHandleDrag,
  onWidthHandleDragEnd,
  onOrientationHandleDrag,
  onOrientationHandleDragEnd,
}: {
  committed: AreaPlacement;
  liveCenter: { lat: number; lng: number } | null;
  iconOrientationDegrees: number;
  color: string;
  paneName: string;
  suspendFitDuringHandleDragRef: MutableRefObject<boolean>;
  onHeightHandleDrag: (areaId: string, lat: number, lng: number) => void;
  onHeightHandleDragEnd: (areaId: string, lat: number, lng: number) => void | Promise<void>;
  onWidthHandleDrag: (areaId: string, lat: number, lng: number) => void;
  onWidthHandleDragEnd: (areaId: string, lat: number, lng: number) => void | Promise<void>;
  onOrientationHandleDrag: (areaId: string, lat: number, lng: number) => void;
  onOrientationHandleDragEnd: (areaId: string, lat: number, lng: number) => void | Promise<void>;
}) {
  const map = useMap();
  const markersRef = useRef<{ h?: L.Marker; w?: L.Marker; o?: L.Marker }>({});
  const committedRef = useRef(committed);
  committedRef.current = committed;
  const orientationHandleDraggingRef = useRef(false);

  const placementSig = `${committed.id}|${committed.latitude}|${committed.longitude}|${committed.widthM}|${committed.heightM}|${committed.orientationDegrees}|${committed.shape}`;

  useEffect(() => {
    const rm = (m: L.Marker | undefined) => {
      if (m) map.removeLayer(m);
    };
    rm(markersRef.current.h);
    rm(markersRef.current.w);
    rm(markersRef.current.o);
    markersRef.current = {};

    const c = committedRef.current;
    const p: AreaPlacement = c;
    const id = c.id;

    const h = L.marker(heightResizeHandlePosition(p), { icon: handleIconHeight(color), draggable: true, pane: paneName }).addTo(map);
    const w = L.marker(widthResizeHandlePosition(p), { icon: handleIconWidth(color), draggable: true, pane: paneName }).addTo(map);
    const o = L.marker(orientationHandlePosition(p), { icon: handleIconOrientation(color, c.orientationDegrees), draggable: true, pane: paneName }).addTo(map);

    const withSuspend = () => {
      suspendFitDuringHandleDragRef.current = true;
    };
    h.on('dragstart', withSuspend);
    w.on('dragstart', withSuspend);
    o.on('dragstart', () => {
      orientationHandleDraggingRef.current = true;
      withSuspend();
    });

    h.on('drag', (e: L.LeafletEvent) => {
      const ll = (e.target as L.Marker).getLatLng();
      onHeightHandleDrag(id, ll.lat, ll.lng);
    });
    h.on('dragend', (e: L.LeafletEvent) => {
      L.DomEvent.stopPropagation(e);
      const ll = (e.target as L.Marker).getLatLng();
      void onHeightHandleDragEnd(id, ll.lat, ll.lng);
    });

    w.on('drag', (e: L.LeafletEvent) => {
      const ll = (e.target as L.Marker).getLatLng();
      onWidthHandleDrag(id, ll.lat, ll.lng);
    });
    w.on('dragend', (e: L.LeafletEvent) => {
      L.DomEvent.stopPropagation(e);
      const ll = (e.target as L.Marker).getLatLng();
      void onWidthHandleDragEnd(id, ll.lat, ll.lng);
    });

    o.on('drag', (e: L.LeafletEvent) => {
      const ll = (e.target as L.Marker).getLatLng();
      onOrientationHandleDrag(id, ll.lat, ll.lng);
    });
    o.on('dragend', (e: L.LeafletEvent) => {
      L.DomEvent.stopPropagation(e);
      const ll = (e.target as L.Marker).getLatLng();
      const cc = committedRef.current;
      orientationHandleDraggingRef.current = false;
      const endDeg = orientationDegreesFromHandleRay(cc.latitude, cc.longitude, ll.lat, ll.lng);
      (e.target as L.Marker).setIcon(handleIconOrientation(color, endDeg));
      void onOrientationHandleDragEnd(id, ll.lat, ll.lng);
    });

    markersRef.current = { h, w, o };
    return () => {
      rm(markersRef.current.h);
      rm(markersRef.current.w);
      rm(markersRef.current.o);
      markersRef.current = {};
    };
  }, [
    map,
    placementSig,
    color,
    paneName,
    suspendFitDuringHandleDragRef,
    onHeightHandleDrag,
    onHeightHandleDragEnd,
    onWidthHandleDrag,
    onWidthHandleDragEnd,
    onOrientationHandleDrag,
    onOrientationHandleDragEnd,
  ]);

  useEffect(() => {
    const o = markersRef.current.o;
    if (!o) return;
    if (orientationHandleDraggingRef.current) {
      if (applyOrientationIconRotationDom(o, iconOrientationDegrees)) return;
    }
    o.setIcon(handleIconOrientation(color, iconOrientationDegrees));
  }, [color, iconOrientationDegrees]);

  useEffect(() => {
    const c = committedRef.current;
    const p: AreaPlacement = liveCenter != null ? { ...c, latitude: liveCenter.lat, longitude: liveCenter.lng } : c;
    markersRef.current.h?.setLatLng(heightResizeHandlePosition(p));
    markersRef.current.w?.setLatLng(widthResizeHandlePosition(p));
    markersRef.current.o?.setLatLng(orientationHandlePosition(p));
  }, [placementSig, liveCenter]);

  return null;
}

export function AreaPlacementLayer({
  areas,
  centerMarkerAreas,
  selectedAreaId,
  editable,
  palette = DEFAULT_AREA_PALETTE,
  panes = {
    areas: 'areasPane',
    areasSelected: 'areasSelectedPane',
    centers: 'areaCentersPane',
    centersSelected: 'areaCentersSelectedPane',
    handles: 'handlesPane',
  },
  ensurePanes = true,
  getPolygonLatLngs,
  getPolygonStyle,
  onSelectArea,
  showCenterMarkers = false,
  onCenterDrag,
  onCenterDragEnd,
  selectedCommittedForHandles,
  selectedLiveCenterForHandles,
  selectedIconOrientationDegreesForHandles,
  handlesColor,
  suspendFitDuringHandleDragRef,
  onHeightHandleDrag,
  onHeightHandleDragEnd,
  onWidthHandleDrag,
  onWidthHandleDragEnd,
  onOrientationHandleDrag,
  onOrientationHandleDragEnd,
}: {
  areas: AreaPlacement[];
  centerMarkerAreas?: AreaPlacement[];
  selectedAreaId: string | null;
  editable: boolean;
  palette?: readonly string[];
  panes?: { areas: string; areasSelected: string; centers: string; centersSelected: string; handles: string };
  ensurePanes?: boolean;
  getPolygonLatLngs?: (a: AreaPlacement) => LatLngExpression[];
  getPolygonStyle?: (a: AreaPlacement, index: number, selected: boolean) => AreaPolygonsStyle;
  onSelectArea?: (areaId: string | null) => void;
  showCenterMarkers?: boolean;
  onCenterDrag?: (areaId: string, lat: number, lng: number) => void;
  onCenterDragEnd?: (areaId: string, lat: number, lng: number) => void | Promise<void>;
  selectedCommittedForHandles?: AreaPlacement | null;
  selectedLiveCenterForHandles?: { lat: number; lng: number } | null;
  selectedIconOrientationDegreesForHandles?: number;
  handlesColor?: string;
  suspendFitDuringHandleDragRef?: MutableRefObject<boolean>;
  onHeightHandleDrag?: (areaId: string, lat: number, lng: number) => void;
  onHeightHandleDragEnd?: (areaId: string, lat: number, lng: number) => void | Promise<void>;
  onWidthHandleDrag?: (areaId: string, lat: number, lng: number) => void;
  onWidthHandleDragEnd?: (areaId: string, lat: number, lng: number) => void | Promise<void>;
  onOrientationHandleDrag?: (areaId: string, lat: number, lng: number) => void;
  onOrientationHandleDragEnd?: (areaId: string, lat: number, lng: number) => void | Promise<void>;
}) {
  const map = useMap();

  useLayoutEffect(() => {
    if (!ensurePanes) return;
    const ensurePane = (name: string, zIndex: number) => {
      const existing = map.getPane(name);
      const p = existing ?? map.createPane(name);
      if (p) p.style.zIndex = String(zIndex);
    };
    ensurePane('sitePane', 420);
    ensurePane(panes.areas, 450);
    ensurePane(panes.areasSelected, 460);
    ensurePane(panes.centers, 465);
    ensurePane(panes.centersSelected, 466);
    ensurePane(panes.handles, 480);
  }, [map, ensurePanes, panes.areas, panes.areasSelected, panes.centers, panes.centersSelected, panes.handles]);

  const resolvedGetPolygonLatLngs =
    getPolygonLatLngs ??
    ((a: AreaPlacement) =>
      a.shape === 'oval'
        ? placementOvalLatLngs(a.latitude, a.longitude, a.widthM, a.heightM, a.orientationDegrees)
        : placementRectangleLatLngs(a.latitude, a.longitude, a.widthM, a.heightM, a.orientationDegrees));

  const resolvedGetPolygonStyle =
    getPolygonStyle ??
    ((_a: AreaPlacement, idx: number, selected: boolean): AreaPolygonsStyle => {
      const color = areaColor(idx, palette);
      return { color, weight: selected ? 4 : 2, fillColor: color, fillOpacity: 0.12 };
    });

  return (
    <>
      {areas.map((a, idx) => {
        const selected = a.id === selectedAreaId;
        const style = resolvedGetPolygonStyle(a, idx, selected);
        const positions = resolvedGetPolygonLatLngs(a);
        return (
          <Polygon
            key={`${a.id}-shape`}
            positions={positions}
            pane={selected ? panes.areasSelected : panes.areas}
            pathOptions={style}
            eventHandlers={{
              click: (e) => {
                L.DomEvent.stopPropagation(e);
                onSelectArea?.(a.id);
              },
            }}
          >
            <LeafletTooltip direction="top" offset={[0, -4]}>
              {a.name}
            </LeafletTooltip>
          </Polygon>
        );
      })}

      {showCenterMarkers ? (
        <ImperativeAreaCenterMarkers
          placed={centerMarkerAreas ?? areas}
          selectedId={selectedAreaId}
          editable={editable}
          paneNames={{ centers: panes.centers, centersSelected: panes.centersSelected }}
          palette={palette}
          onSelect={(id) => onSelectArea?.(id)}
          onCenterDrag={onCenterDrag}
          onCenterDragEnd={onCenterDragEnd}
          suspendFitDuringHandleDragRef={suspendFitDuringHandleDragRef}
        />
      ) : null}

      {editable &&
      selectedCommittedForHandles &&
      selectedAreaId &&
      typeof selectedIconOrientationDegreesForHandles === 'number' &&
      handlesColor &&
      suspendFitDuringHandleDragRef &&
      onHeightHandleDrag &&
      onHeightHandleDragEnd &&
      onWidthHandleDrag &&
      onWidthHandleDragEnd &&
      onOrientationHandleDrag &&
      onOrientationHandleDragEnd ? (
        <ImperativeResizeHandleMarkers
          committed={selectedCommittedForHandles}
          liveCenter={selectedLiveCenterForHandles ?? null}
          iconOrientationDegrees={selectedIconOrientationDegreesForHandles}
          color={handlesColor}
          paneName={panes.handles}
          suspendFitDuringHandleDragRef={suspendFitDuringHandleDragRef}
          onHeightHandleDrag={onHeightHandleDrag}
          onHeightHandleDragEnd={onHeightHandleDragEnd}
          onWidthHandleDrag={onWidthHandleDrag}
          onWidthHandleDragEnd={onWidthHandleDragEnd}
          onOrientationHandleDrag={onOrientationHandleDrag}
          onOrientationHandleDragEnd={onOrientationHandleDragEnd}
        />
      ) : null}
    </>
  );
}

